import { useMutation, useQuery } from "@tanstack/react-query";
import { handleError } from "@/api/error";
import { queryClient } from "@/providers/QueryClientProviderWrapper";
import { client, useScopedMerchantClient } from "./scoped-merchant-client";

const BATCH_STATUS_CHUNK_SIZE = 100;

/**
 * Invalidates every query that reflects a video's ad-campaign membership so all surfaces refresh
 * after a membership change (removal or restore) (UNB-2024). This covers:
 * - the "In ads campaign" pill (`video-campaigns-batch-status`),
 * - the drawer's campaign list (`video-campaigns`),
 * - the overview counts and video-explorer thumbnail badge (`video-overview`),
 * - the product videos-tab thumbnail badge (`product`/`resources`),
 * - the assurance-table thumbnail "Ad" badge + per-video detail (`assurance-video`),
 * - the video detail (`video`).
 *
 * Shared by the drawer mutations, the synchronous `take` path, the review path and the async SSE
 * completion handler so every removal entry point refreshes the same state.
 *
 * The `assurance-video` prefix is always invalidated (not video-scoped): the assurance table's
 * thumbnail "Ad" badge is driven by `isInAdCampaign` on the paginated LIST response, so a
 * video-scoped key alone would leave the badge stale after a removal.
 */
export const invalidateCampaignQueries = (
  merchantId: string | null | undefined,
  workspaceId: string | null,
  videoId?: string,
) => {
  if (!merchantId) return;
  queryClient.invalidateQueries({ queryKey: ["video-campaigns-batch-status", merchantId, workspaceId] });
  queryClient.invalidateQueries({ queryKey: ["video-overview", merchantId, workspaceId] });
  queryClient.invalidateQueries({ queryKey: ["product", "resources", merchantId, workspaceId] });
  queryClient.invalidateQueries({ queryKey: ["assurance-video", merchantId, workspaceId] });
  if (videoId) {
    queryClient.invalidateQueries({ queryKey: ["video-campaigns", merchantId, workspaceId, videoId] });
    queryClient.invalidateQueries({ queryKey: ["video", merchantId, workspaceId, videoId] });
  } else {
    queryClient.invalidateQueries({ queryKey: ["video-campaigns", merchantId, workspaceId] });
    queryClient.invalidateQueries({ queryKey: ["video", merchantId, workspaceId] });
  }
};

export const useVideoCampaignsBatchStatus = (videoIds: string[]) => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: ["video-campaigns-batch-status", scope.merchantId, scope.workspaceId, videoIds],
    queryFn: async () => {
      const chunks: string[][] = [];
      for (let i = 0; i < videoIds.length; i += BATCH_STATUS_CHUNK_SIZE) {
        chunks.push(videoIds.slice(i, i + BATCH_STATUS_CHUNK_SIZE));
      }

      const results = await Promise.all(
        chunks.map(async (chunk) => {
          const res = scope.workspaceId
            ? await client.workspace[":workspaceId"].merchant[":merchantId"].campaigns["batch-status"].$post({
                param: scope.param,
                json: { videoIds: chunk },
              })
            : await client.merchant[":merchantId"].campaigns["batch-status"].$post({
                param: scope.param,
                json: { videoIds: chunk },
              });
          if (!res.ok) throw await handleError(res);
          return res.json();
        }),
      );

      return Object.assign({}, ...results) as (typeof results)[0];
    },
    enabled: !!scope.merchantId && videoIds.length > 0,
  });
};

export const useVideoCampaigns = (videoId?: string) => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: ["video-campaigns", scope.merchantId, scope.workspaceId, videoId],
    queryFn: async () => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].campaigns.video[":videoId"].$get({
            param: { ...scope.param, videoId: videoId as string },
          })
        : await client.merchant[":merchantId"].campaigns.video[":videoId"].$get({
            param: { ...scope.param, videoId: videoId as string },
          });
      if (!res.ok) throw await handleError(res);
      return res.json();
    },
    enabled: !!scope.merchantId && !!videoId,
  });
};

export const useRemoveFromCampaign = (videoId: string) => {
  const scope = useScopedMerchantClient();

  return useMutation({
    mutationFn: async ({ campaignId }: { campaignId: string }) => {
      if (!scope.merchantId) throw new Error("Missing merchant context");
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].campaigns[":campaignId"].video[
            ":videoId"
          ].$delete({
            param: { ...scope.param, campaignId, videoId },
          })
        : await client.merchant[":merchantId"].campaigns[":campaignId"].video[":videoId"].$delete({
            param: { ...scope.param, campaignId, videoId },
          });
      if (!res.ok) throw await handleError(res);
      return res.json();
    },
    onSuccess: () => {
      invalidateCampaignQueries(scope.merchantId, scope.workspaceId, videoId);
    },
  });
};

export const useRemoveFromAllCampaigns = (videoId: string) => {
  const scope = useScopedMerchantClient();

  return useMutation({
    mutationFn: async () => {
      if (!scope.merchantId) throw new Error("Missing merchant context");
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].campaigns.video[":videoId"].all.$delete({
            param: { ...scope.param, videoId },
          })
        : await client.merchant[":merchantId"].campaigns.video[":videoId"].all.$delete({
            param: { ...scope.param, videoId },
          });
      if (!res.ok) throw await handleError(res);
      return res.json();
    },
    onSuccess: () => {
      invalidateCampaignQueries(scope.merchantId, scope.workspaceId, videoId);
    },
  });
};

export const useRestoreToCampaign = (videoId: string) => {
  const scope = useScopedMerchantClient();

  return useMutation({
    mutationFn: async ({ campaignId }: { campaignId: string }) => {
      if (!scope.merchantId) throw new Error("Missing merchant context");
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].campaigns[":campaignId"].video[
            ":videoId"
          ].restore.$post({
            param: { ...scope.param, campaignId, videoId },
          })
        : await client.merchant[":merchantId"].campaigns[":campaignId"].video[":videoId"].restore.$post({
            param: { ...scope.param, campaignId, videoId },
          });
      if (!res.ok) throw await handleError(res);
      return res.json();
    },
    onSuccess: () => {
      invalidateCampaignQueries(scope.merchantId, scope.workspaceId, videoId);
    },
  });
};

export const useRestoreToAllCampaigns = (videoId: string) => {
  const scope = useScopedMerchantClient();

  return useMutation({
    mutationFn: async () => {
      if (!scope.merchantId) throw new Error("Missing merchant context");
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].campaigns.video[":videoId"][
            "restore-all"
          ].$post({
            param: { ...scope.param, videoId },
          })
        : await client.merchant[":merchantId"].campaigns.video[":videoId"]["restore-all"].$post({
            param: { ...scope.param, videoId },
          });
      if (!res.ok) throw await handleError(res);
      return res.json();
    },
    onSuccess: () => {
      invalidateCampaignQueries(scope.merchantId, scope.workspaceId, videoId);
    },
  });
};

export const useRemoveVideoFromCampaign = () => {
  const scope = useScopedMerchantClient();

  return useMutation({
    mutationFn: async ({ videoId, campaignId }: { videoId: string; campaignId: string }) => {
      if (!scope.merchantId) throw new Error("Missing merchant context");
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].campaigns[":campaignId"].video[
            ":videoId"
          ].$delete({
            param: { ...scope.param, campaignId, videoId },
          })
        : await client.merchant[":merchantId"].campaigns[":campaignId"].video[":videoId"].$delete({
            param: { ...scope.param, campaignId, videoId },
          });
      if (!res.ok) throw await handleError(res);
      return res.json();
    },
    onSuccess: (_data, { videoId }) => {
      invalidateCampaignQueries(scope.merchantId, scope.workspaceId, videoId);
    },
  });
};

export const useRemoveVideoFromAllCampaigns = () => {
  const scope = useScopedMerchantClient();

  return useMutation({
    mutationFn: async ({ videoId }: { videoId: string }) => {
      if (!scope.merchantId) throw new Error("Missing merchant context");
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].campaigns.video[":videoId"].all.$delete({
            param: { ...scope.param, videoId },
          })
        : await client.merchant[":merchantId"].campaigns.video[":videoId"].all.$delete({
            param: { ...scope.param, videoId },
          });
      if (!res.ok) throw await handleError(res);
      return res.json();
    },
    onSuccess: (_data, { videoId }) => {
      invalidateCampaignQueries(scope.merchantId, scope.workspaceId, videoId);
    },
  });
};
